import { useMemo, useState } from 'react';
import { CheckCircle2, Droplets, Loader2, Plus, ShieldAlert, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DataErrorBanner, type DataErrorDetail } from '@/components/ui/data-error-banner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  formatDateTime,
  formatMoney,
  type DepotContainerVisit,
  type DepotRepairEstimate,
  type DepotRepairJob,
  type DepotWashJob,
  useDepotList,
} from './depot-hooks';
import {
  DepotEmptyRow,
  DepotGridPanel,
  depotTableClassName,
  depotTableHeadClassName,
  depotTdClassName,
  depotThClassName,
} from './depot-grid';

type EstimateForm = {
  visitId: string;
  depotId: string;
  damageSummary: string;
  laborCost: string;
  materialCost: string;
};

type RepairJobForm = {
  visitId: string;
  depotId: string;
  repairEstimateId: string;
  repairSummary: string;
  assignedTo: string;
  laborHours: string;
  materialCost: string;
};

type WashJobForm = {
  visitId: string;
  depotId: string;
  washType: string;
  assignedTo: string;
  detergentType: string;
  notes: string;
};

const EMPTY_ESTIMATE: EstimateForm = {
  visitId: '',
  depotId: '',
  damageSummary: '',
  laborCost: '0',
  materialCost: '0',
};

const EMPTY_REPAIR_JOB: RepairJobForm = {
  visitId: '',
  depotId: '',
  repairEstimateId: '',
  repairSummary: '',
  assignedTo: '',
  laborHours: '0',
  materialCost: '0',
};

const EMPTY_WASH_JOB: WashJobForm = {
  visitId: '',
  depotId: '',
  washType: 'standard',
  assignedTo: '',
  detergentType: '',
  notes: '',
};

export default function DepotMaintenancePage() {
  const estimates = useDepotList<DepotRepairEstimate>('/depot/repair-estimates?limit=100');
  const repairJobs = useDepotList<DepotRepairJob>('/depot/repair-jobs?limit=100');
  const washJobs = useDepotList<DepotWashJob>('/depot/wash-jobs?limit=100');
  const visits = useDepotList<DepotContainerVisit>('/depot/containers?limit=100');

  const [estimateOpen, setEstimateOpen] = useState(false);
  const [repairOpen, setRepairOpen] = useState(false);
  const [washOpen, setWashOpen] = useState(false);
  const [estimateForm, setEstimateForm] = useState<EstimateForm>(EMPTY_ESTIMATE);
  const [repairForm, setRepairForm] = useState<RepairJobForm>(EMPTY_REPAIR_JOB);
  const [washForm, setWashForm] = useState<WashJobForm>(EMPTY_WASH_JOB);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const pageError: DataErrorDetail | null = estimates.error
    ? { endpoint: '/depot/repair-estimates', message: estimates.error, status: 500, code: 'DEPOT_MAINTENANCE_LOAD_FAILED' }
    : null;

  const activeVisits = useMemo(
    () => visits.items.filter((visit) => visit.visit_status !== 'completed'),
    [visits.items],
  );

  const repairEligibleVisits = useMemo(
    () => activeVisits.filter((visit) => ['repair_required', 'damaged', 'reinspection_pending'].includes(visit.condition_status)),
    [activeVisits],
  );

  const washEligibleVisits = useMemo(
    () => activeVisits.filter((visit) => ['wash_required', 'repair_required', 'damaged'].includes(visit.condition_status)),
    [activeVisits],
  );

  const refreshAll = async () => {
    await Promise.all([estimates.refresh(), repairJobs.refresh(), washJobs.refresh(), visits.refresh()]);
  };

  const handleCreateEstimate = async () => {
    if (!estimateForm.visitId || !estimateForm.depotId || !estimateForm.damageSummary.trim()) {
      toast.error('Visit and damage summary are required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/depot/repair-estimates', {
        visitId: estimateForm.visitId,
        depotId: estimateForm.depotId,
        damageSummary: estimateForm.damageSummary.trim(),
        laborCost: Number(estimateForm.laborCost) || 0,
        materialCost: Number(estimateForm.materialCost) || 0,
        approvalRequired: true,
      });
      toast.success('Repair estimate created');
      setEstimateOpen(false);
      setEstimateForm(EMPTY_ESTIMATE);
      await refreshAll();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create repair estimate');
    } finally {
      setSaving(false);
    }
  };

  const handleRequestApproval = async (estimateId: string) => {
    setBusyId(estimateId);
    try {
      const approval = await api.post<{ id: string }>(`/depot/repair-estimates/${estimateId}/request-approval`, {});
      toast.success(`Approval ${approval.id} created`);
      await estimates.refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to request repair approval');
    } finally {
      setBusyId(null);
    }
  };

  const handleCreateRepairJob = async () => {
    if (!repairForm.visitId || !repairForm.depotId || !repairForm.repairSummary.trim()) {
      toast.error('Visit and repair summary are required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/depot/repair-jobs', {
        visitId: repairForm.visitId,
        depotId: repairForm.depotId,
        repairEstimateId: repairForm.repairEstimateId || undefined,
        repairSummary: repairForm.repairSummary.trim(),
        assignedTo: repairForm.assignedTo.trim() || undefined,
        laborHours: Number(repairForm.laborHours) || 0,
        materialCost: Number(repairForm.materialCost) || 0,
      });
      toast.success('Repair job created');
      setRepairOpen(false);
      setRepairForm(EMPTY_REPAIR_JOB);
      await refreshAll();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create repair job');
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteRepairJob = async (jobId: string) => {
    setBusyId(jobId);
    try {
      await api.post(`/depot/repair-jobs/${jobId}/complete`, {});
      toast.success('Repair job completed');
      await refreshAll();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to complete repair job');
    } finally {
      setBusyId(null);
    }
  };

  const handleCreateWashJob = async () => {
    if (!washForm.visitId || !washForm.depotId) {
      toast.error('Visit is required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/depot/wash-jobs', {
        visitId: washForm.visitId,
        depotId: washForm.depotId,
        washType: washForm.washType,
        assignedTo: washForm.assignedTo.trim() || undefined,
        detergentType: washForm.detergentType.trim() || undefined,
        notes: washForm.notes.trim() || undefined,
      });
      toast.success('Wash job created');
      setWashOpen(false);
      setWashForm(EMPTY_WASH_JOB);
      await refreshAll();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create wash job');
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteWashJob = async (jobId: string) => {
    setBusyId(jobId);
    try {
      await api.post(`/depot/wash-jobs/${jobId}/complete`, {});
      toast.success('Wash job completed');
      await refreshAll();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to complete wash job');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-5">
      {pageError && <DataErrorBanner error={pageError} entityLabel="Depot Maintenance" onRetry={refreshAll} />}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Maintenance Desk</h2>
          <p className="text-sm text-muted-foreground">
            Manage repair estimates, repair execution, wash jobs, and approval gating.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setEstimateOpen(true)}>
            <ShieldAlert className="mr-1.5 h-4 w-4" />
            New Estimate
          </Button>
          <Button variant="outline" onClick={() => setRepairOpen(true)}>
            <Wrench className="mr-1.5 h-4 w-4" />
            New Repair Job
          </Button>
          <Button onClick={() => setWashOpen(true)}>
            <Droplets className="mr-1.5 h-4 w-4" />
            New Wash Job
          </Button>
        </div>
      </div>

      <DepotGridPanel title="Repair Estimates" subtitle="Estimate and approval queue for damaged containers.">
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Estimate</th>
              <th className={depotThClassName}>Container</th>
              <th className={depotThClassName}>Damage</th>
              <th className={depotThClassName}>Cost</th>
              <th className={depotThClassName}>Status</th>
              <th className={depotThClassName}>Requested</th>
              <th className={depotThClassName}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {estimates.items.length === 0 ? (
              <DepotEmptyRow colSpan={7}>No repair estimates yet.</DepotEmptyRow>
            ) : (
              estimates.items.map((item) => (
                <tr key={item.id} className="hover:bg-muted/30">
                  <td className={`${depotTdClassName} font-mono font-semibold`}>{item.estimate_no}</td>
                  <td className={depotTdClassName}>{item.container_no} · {item.depot_code ?? '—'}</td>
                  <td className={depotTdClassName}>{item.damage_summary ?? '—'}</td>
                  <td className={depotTdClassName}>
                    <div>Total {formatMoney(item.total_amount, item.currency)}</div>
                    <div className="text-xs text-muted-foreground">
                      Labor {formatMoney(item.labor_cost, item.currency)} · Material {formatMoney(item.material_cost, item.currency)}
                    </div>
                  </td>
                  <td className={depotTdClassName}>
                    <div className="flex flex-wrap gap-1">
                      <Badge variant="outline">{item.estimate_status}</Badge>
                      <Badge variant={item.approval_status === 'approved' ? 'secondary' : item.approval_status === 'pending' ? 'outline' : 'destructive'}>
                        {item.approval_status}
                      </Badge>
                    </div>
                  </td>
                  <td className={`${depotTdClassName} text-xs text-muted-foreground`}>{formatDateTime(item.approval_requested_at)}</td>
                  <td className={depotTdClassName}>
                    {item.approval_required && item.approval_status === 'not_requested' ? (
                      <Button size="sm" variant="outline" onClick={() => handleRequestApproval(item.id)} disabled={busyId === item.id}>
                        {busyId === item.id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ShieldAlert className="mr-1.5 h-4 w-4" />}
                        Request Approval
                      </Button>
                    ) : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DepotGridPanel>

      <DepotGridPanel title="Repair Jobs" subtitle="Execution queue for approved repair work.">
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Job</th>
              <th className={depotThClassName}>Container</th>
              <th className={depotThClassName}>Summary</th>
              <th className={depotThClassName}>Assigned</th>
              <th className={depotThClassName}>Costing</th>
              <th className={depotThClassName}>Timing</th>
              <th className={depotThClassName}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {repairJobs.items.length === 0 ? (
              <DepotEmptyRow colSpan={7}>No repair jobs yet.</DepotEmptyRow>
            ) : (
              repairJobs.items.map((item) => (
                <tr key={item.id} className="hover:bg-muted/30">
                  <td className={`${depotTdClassName} font-mono font-semibold`}>{item.repair_job_no}</td>
                  <td className={depotTdClassName}>{item.container_no}</td>
                  <td className={depotTdClassName}>{item.repair_summary ?? '—'}</td>
                  <td className={depotTdClassName}>{item.assigned_to ?? 'Unassigned'}</td>
                  <td className={depotTdClassName}>Hours {item.labor_hours} · Material {formatMoney(item.material_cost)}</td>
                  <td className={`${depotTdClassName} text-xs text-muted-foreground`}>
                    <div>Started {formatDateTime(item.started_at)}</div>
                    <div>Completed {formatDateTime(item.completed_at)}</div>
                  </td>
                  <td className={depotTdClassName}>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={item.job_status === 'completed' ? 'secondary' : 'outline'}>{item.job_status}</Badge>
                      {item.job_status !== 'completed' ? (
                        <Button size="sm" variant="outline" onClick={() => handleCompleteRepairJob(item.id)} disabled={busyId === item.id}>
                          {busyId === item.id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />}
                          Complete
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DepotGridPanel>

      <DepotGridPanel title="Wash Jobs" subtitle="Cleaning and wash execution queue.">
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Job</th>
              <th className={depotThClassName}>Container</th>
              <th className={depotThClassName}>Wash Type</th>
              <th className={depotThClassName}>Assigned / Detergent</th>
              <th className={depotThClassName}>Timing</th>
              <th className={depotThClassName}>Notes</th>
              <th className={depotThClassName}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {washJobs.items.length === 0 ? (
              <DepotEmptyRow colSpan={7}>No wash jobs yet.</DepotEmptyRow>
            ) : (
              washJobs.items.map((item) => (
                <tr key={item.id} className="hover:bg-muted/30">
                  <td className={`${depotTdClassName} font-mono font-semibold`}>{item.wash_job_no}</td>
                  <td className={depotTdClassName}>{item.container_no}</td>
                  <td className={depotTdClassName}>{item.wash_type}</td>
                  <td className={depotTdClassName}>{item.assigned_to ?? 'Unassigned'} · {item.detergent_type ?? 'Detergent TBC'}</td>
                  <td className={`${depotTdClassName} text-xs text-muted-foreground`}>
                    <div>Started {formatDateTime(item.started_at)}</div>
                    <div>Completed {formatDateTime(item.completed_at)}</div>
                  </td>
                  <td className={depotTdClassName}>{item.notes ?? '—'}</td>
                  <td className={depotTdClassName}>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={item.job_status === 'completed' ? 'secondary' : 'outline'}>{item.job_status}</Badge>
                      {item.job_status !== 'completed' ? (
                        <Button size="sm" variant="outline" onClick={() => handleCompleteWashJob(item.id)} disabled={busyId === item.id}>
                          {busyId === item.id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />}
                          Complete
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DepotGridPanel>

      <Dialog open={estimateOpen} onOpenChange={setEstimateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Repair Estimate</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <Select value={estimateForm.visitId} onValueChange={(value) => {
              const selected = repairEligibleVisits.find((visit) => visit.id === value);
              setEstimateForm((current) => ({ ...current, visitId: value, depotId: selected?.depot_id ?? current.depotId }));
            }}>
              <SelectTrigger><SelectValue placeholder="Select repair-required visit" /></SelectTrigger>
              <SelectContent>
                {repairEligibleVisits.map((visit) => (
                  <SelectItem key={visit.id} value={visit.id}>{visit.container_no} · {visit.visit_ref}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input placeholder="Damage Summary" value={estimateForm.damageSummary} onChange={(event) => setEstimateForm((current) => ({ ...current, damageSummary: event.target.value }))} />
            <Input placeholder="Labor Cost" value={estimateForm.laborCost} onChange={(event) => setEstimateForm((current) => ({ ...current, laborCost: event.target.value }))} />
            <Input placeholder="Material Cost" value={estimateForm.materialCost} onChange={(event) => setEstimateForm((current) => ({ ...current, materialCost: event.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEstimateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateEstimate} disabled={saving}>
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}
              Create Estimate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={repairOpen} onOpenChange={setRepairOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Repair Job</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <Select value={repairForm.visitId} onValueChange={(value) => {
              const selected = repairEligibleVisits.find((visit) => visit.id === value);
              setRepairForm((current) => ({ ...current, visitId: value, depotId: selected?.depot_id ?? current.depotId }));
            }}>
              <SelectTrigger><SelectValue placeholder="Select visit" /></SelectTrigger>
              <SelectContent>
                {repairEligibleVisits.map((visit) => (
                  <SelectItem key={visit.id} value={visit.id}>{visit.container_no} · {visit.visit_ref}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={repairForm.repairEstimateId} onValueChange={(value) => setRepairForm((current) => ({ ...current, repairEstimateId: value }))}>
              <SelectTrigger><SelectValue placeholder="Optional estimate" /></SelectTrigger>
              <SelectContent>
                {estimates.items.filter((item) => item.approval_status === 'approved' || item.approval_required === false).map((item) => (
                  <SelectItem key={item.id} value={item.id}>{item.estimate_no} · {item.container_no}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input placeholder="Repair Summary" value={repairForm.repairSummary} onChange={(event) => setRepairForm((current) => ({ ...current, repairSummary: event.target.value }))} />
            <Input placeholder="Assigned To" value={repairForm.assignedTo} onChange={(event) => setRepairForm((current) => ({ ...current, assignedTo: event.target.value }))} />
            <Input placeholder="Labor Hours" value={repairForm.laborHours} onChange={(event) => setRepairForm((current) => ({ ...current, laborHours: event.target.value }))} />
            <Input placeholder="Material Cost" value={repairForm.materialCost} onChange={(event) => setRepairForm((current) => ({ ...current, materialCost: event.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRepairOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateRepairJob} disabled={saving}>
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Wrench className="mr-1.5 h-4 w-4" />}
              Create Repair Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={washOpen} onOpenChange={setWashOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Wash Job</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <Select value={washForm.visitId} onValueChange={(value) => {
              const selected = washEligibleVisits.find((visit) => visit.id === value);
              setWashForm((current) => ({ ...current, visitId: value, depotId: selected?.depot_id ?? current.depotId }));
            }}>
              <SelectTrigger><SelectValue placeholder="Select visit" /></SelectTrigger>
              <SelectContent>
                {washEligibleVisits.map((visit) => (
                  <SelectItem key={visit.id} value={visit.id}>{visit.container_no} · {visit.visit_ref}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input placeholder="Wash Type" value={washForm.washType} onChange={(event) => setWashForm((current) => ({ ...current, washType: event.target.value }))} />
            <Input placeholder="Assigned To" value={washForm.assignedTo} onChange={(event) => setWashForm((current) => ({ ...current, assignedTo: event.target.value }))} />
            <Input placeholder="Detergent Type" value={washForm.detergentType} onChange={(event) => setWashForm((current) => ({ ...current, detergentType: event.target.value }))} />
            <Input placeholder="Notes" value={washForm.notes} onChange={(event) => setWashForm((current) => ({ ...current, notes: event.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWashOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateWashJob} disabled={saving}>
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Droplets className="mr-1.5 h-4 w-4" />}
              Create Wash Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
